/**
 * SynonymSection - Collapsible category section for thesaurus synonyms
 *
 * Displays category header with collapse/expand, word count, and description.
 * Renders grid of SynonymChip components for synonym words.
 */

import React, { useState } from 'react'
import { SynonymChip } from './SynonymChip'
import { useWordActions } from '../../hooks/useWordActions'
import type { DiscoveryMetadata } from '../../types/wordCollection'

interface SynonymSectionProps {
  /** Category title for display */
  title: string
  /** Short description of the category */
  description: string
  /** Array of synonym words */
  synonyms: string[]
  /** Original word searched in thesaurus */
  sourceWord: string
  /** Sense label for provenance tracking */
  senseLabel: string
  /** Category name for provenance tracking */
  category: string
}

/**
 * Collapsible section for a synonym category.
 *
 * Features:
 * - Collapse/expand toggle
 * - Word count in header
 * - Category description
 * - Grid of collection-aware synonym chips
 * - Thesaurus provenance tracking
 */
export const SynonymSection: React.FC<SynonymSectionProps> = ({
  title,
  description,
  synonyms,
  sourceWord,
  senseLabel,
  category
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  // Use centralized word actions
  const { collectWord, uncollectWord, isCollected } = useWordActions()

  // Deduplicate synonyms (case-insensitive) to prevent multiple chips for same word
  const uniqueSynonyms = [...new Set(synonyms.map(s => s.toLowerCase()))]
    .map(lower => synonyms.find(s => s.toLowerCase() === lower)!)

  // Don't render if no synonyms
  if (uniqueSynonyms.length === 0) {
    return null
  }

  // Toggle collection for a synonym - adds or removes with thesaurus provenance
  const handleToggleCollection = (synonymWord: string) => {
    if (isCollected(synonymWord)) {
      // Remove from collection (uncollectWord accepts word text)
      uncollectWord(synonymWord)
    } else {
      // Add word with thesaurus provenance
      const provenance: DiscoveryMetadata = {
        method: 'thesaurus_lookup',
        thesaurus_data: {
          source_word: sourceWord,
          index_entry: synonymWord,
          sense: senseLabel,
          category: category
        }
      }
      collectWord(synonymWord, provenance)
    }
  }

  // Generate slug for category (for testid)
  const categorySlug = category.toLowerCase().replace(/\s+/g, '-')

  return (
    <div
      data-testid={`synonym-section-${categorySlug}`}
      className="border border-gray-200 rounded-lg p-3"
    >
      {/* Header with toggle */}
      <button
        data-testid={`synonym-section-toggle-${categorySlug}`}
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors"
        aria-expanded={isExpanded}
        aria-controls={`category-content-${categorySlug}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          </div>
          <p className="text-xs text-gray-500">{description}</p>
        </div>

        <div className="flex items-center gap-3 ml-4">
          <span className="text-xs font-medium text-gray-500">
            {uniqueSynonyms.length} {uniqueSynonyms.length === 1 ? 'word' : 'words'}
          </span>
          <span className="text-gray-400 text-sm">{isExpanded ? '▼' : '▶'}</span>
        </div>
      </button>

      {/* Synonym chips */}
      {isExpanded && (
        <div
          id={`category-content-${categorySlug}`}
          className="mt-3"
        >
          <div className="flex flex-wrap gap-2">
            {uniqueSynonyms.map(word => (
              <SynonymChip
                key={word}
                word={word}
                isCollected={isCollected(word)}
                onClick={() => handleToggleCollection(word)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
